import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@repo/ui/primitives/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/primitives/card";
import { Input } from "@repo/ui/primitives/input";
import { Label } from "@repo/ui/primitives/label";
import { authIntegration } from "@/lib/api/client";

export default function ResetPasswordPage() {
  const { t } = useTranslation("login");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError(t("resetPassword.missingToken", { defaultValue: "Reset token is missing" }));
      return;
    }
    if (password.length < 6) {
      setError(t("form.validation.passwordMin", { defaultValue: "Password must be at least 6 characters" }));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("resetPassword.mismatch", { defaultValue: "Passwords do not match" }));
      return;
    }
    setLoading(true);
    try {
      const result = await authIntegration.resetPassword({
        token,
        password,
        confirmPassword,
      });
      if (result.success) {
        navigate("/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-svh items-center justify-center bg-background p-[var(--ds-space-6)] text-foreground">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t("resetPassword.title", { defaultValue: "Set a new password" })}</CardTitle>
          <CardDescription>
            {t("resetPassword.description", {
              defaultValue: "Choose a new password for your account.",
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-[var(--ds-space-4)]">
            <div className="space-y-2">
              <Label htmlFor="new-password">{t("form.passwordLabel", { defaultValue: "Password" })}</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">
                {t("resetPassword.confirmLabel", { defaultValue: "Confirm password" })}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p className="text-sm text-[var(--destructive-tint-foreground)]" role="alert">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading
                ? t("resetPassword.submitLoading", { defaultValue: "Saving…" })
                : t("resetPassword.submit", { defaultValue: "Update password" })}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate("/login")}>
              {t("resetPassword.backToLogin", { defaultValue: "Back to login" })}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
